import { openFile } from "@/file";
import globals from "@/globals";
import { saveSettings, uploadSettings } from "@/settings";
import * as signals from "@/signals";
import { deleteBackup, getSuffix } from "@/site";
import { SiteContent } from "@/types";
import { showToast, sleep } from "@/utils";
import loader from "@monaco-editor/loader";
import i18next, { t } from "i18next";
import * as Monaco from "monaco-editor/esm/vs/editor/editor.api";

const codeTextArea = document.getElementById("code") as HTMLTextAreaElement;

let fontSizeTimeout: number | undefined = undefined;

export let editorInstance: Monaco.editor.IStandaloneCodeEditor | null = null;
export let monaco: typeof Monaco;

export function changeEditorFontSize(save: boolean): void {
	if (editorInstance) {
		editorInstance.updateOptions({
			fontSize: globals.settings.fontSize,
		});
	} else {
		const code = document.getElementById("code") as HTMLTextAreaElement;
		code.style.fontSize = globals.settings.fontSize + "px";
	}
	if (save) {
		saveSettings();
		if (globals.privilege) {
			clearTimeout(fontSizeTimeout);
			fontSizeTimeout = window.setTimeout(() => {
				void uploadSettings(
					"fontSize",
					globals.settings.fontSize.toString(),
				);
			}, 1000);
		}
	}
}

export function clearModels(skipDefault: boolean): void {
	const models = monaco.editor.getModels();
	for (const model of models) {
		if (skipDefault && model.uri.path === "/") {
			continue;
		}
		model.dispose();
	}
}

export function codeChanged(): void {
	if (
		!signals.currentFile.value ||
		!signals.files.value ||
		!signals.sites.value
	) {
		return;
	}
	const currentFileContent = getValue();
	signals.files.value = {
		...signals.files.value,
		[signals.currentFile.value]: currentFileContent,
	};
	const initialFileContent =
		globals.currentHostInitial[signals.currentFile.value];
	if (
		currentFileContent &&
		typeof initialFileContent === "string" &&
		currentFileContent.replace(/\s/g, "") !==
			initialFileContent.replace(/\s/g, "")
	) {
		if (!signals.changedFiles.value.includes(signals.currentFile.value)) {
			signals.changedFiles.value = [
				...signals.changedFiles.value,
				signals.currentFile.value,
			];
		}
		signals.hasUnsaved.value = true;
		try {
			localStorage.setItem(
				"CurrentHost",
				JSON.stringify(signals.files.value),
			);
			localStorage.setItem("CurrentHostname", signals.currentSite.value);
		} catch (error) {
			console.error(error);
		}
	} else {
		deleteBackup();
	}
}

export function createModel(filename: string, content: string = ""): void {
	if (
		!editorInstance ||
		(!filename.endsWith(".ts") && !filename.endsWith(".tsx"))
	) {
		return;
	}
	const newModel = monaco.editor.createModel(
		content,
		"typescript",
		monaco.Uri.file(filename),
	);
	newModel.onDidChangeContent(codeChanged);
}

export function createModels(files: Readonly<SiteContent>): void {
	if (!editorInstance) {
		return;
	}
	clearModels(true);
	for (const filename in files) {
		const file = files[filename];
		if (typeof file !== "string") {
			continue;
		}
		createModel(filename, file);
	}
}

function createMonacoEditor(): void {
	const codeEditor = document.getElementById("code-editor") as HTMLDivElement;
	const defaultModel = monaco.editor.createModel(
		codeTextArea.value,
		readMode(true),
		monaco.Uri.file("/"),
	);
	defaultModel.onDidChangeContent(codeChanged);
	editorInstance = monaco.editor.create(codeEditor, {
		automaticLayout: true,
		fontSize: globals.settings.fontSize || 14,
		insertSpaces: false,
		model: defaultModel,
		scrollBeyondLastLine: false, // for AI coding visual experience
		theme: globals.isLight ? "vs" : "vs-dark",
		unicodeHighlight: {
			ambiguousCharacters: false,
		},
		wordWrap: "on",
	});
	const codeService = (
		editorInstance as unknown as {
			_codeEditorService: {
				openCodeEditor: (
					input: {
						resource: {
							path: string;
						};
					},
					source: unknown,
				) => Promise<unknown>;
			};
		}
	)._codeEditorService;
	if (codeService) {
		const openEditorBase = codeService.openCodeEditor.bind(codeService);
		codeService.openCodeEditor = async (
			input,
			source,
		): Promise<unknown> => {
			void openFile(input.resource.path.substring(1));
			return await openEditorBase(input, source);
		};
	}
}

export function deleteModels(filesToBeDeleted: Set<string>): void {
	if (!editorInstance) {
		return;
	}
	const models = monaco.editor.getModels();
	for (const model of models) {
		if (filesToBeDeleted.has(model.uri.path.substring(1))) {
			model.dispose();
		}
	}
}

export async function enableMonacoEditor(delay = 0): Promise<void> {
	if (!globals.isMonacoSupported) {
		return;
	}
	codeTextArea.hidden = true;
	codeTextArea.removeEventListener("input", codeChanged);
	loader.config({
		paths: {
			vs: "https://registry.npmmirror.com/monaco-editor/0.52.2/files/min/vs",
		},
	});
	if (i18next.language !== "en-US") {
		loader.config({
			"vs/nls": {
				availableLanguages: {
					"*": i18next.language.toLowerCase(),
				},
			},
		});
	}
	monaco = await loader.init();
	if (delay) {
		await sleep(delay);
	}
	createMonacoEditor();
	monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
		allowImportingTsExtensions: true,
		allowNonTsExtensions: true,
		baseUrl: "file:///",
		module: monaco.languages.typescript.ModuleKind.ESNext,
		moduleResolution:
			monaco.languages.typescript.ModuleResolutionKind.NodeJs,
		target: monaco.languages.typescript.ScriptTarget.ESNext,
	});
	if (signals.currentSite.value) {
		createModels(signals.files.value);
		setModel(signals.currentFile.value);
	}
	signals.isMonacoEnabled.value = true;
}

export function getSelection(): string {
	if (!editorInstance) {
		return "";
	}
	const selection = editorInstance.getSelection();
	if (!selection) {
		return "";
	}
	return editorInstance.getModel()?.getValueInRange(selection) || "";
}

export function getValue(): string {
	let value = editorInstance ? editorInstance.getValue() : codeTextArea.value;
	const { SUFFIXES } = globals;
	for (const key in SUFFIXES) {
		const suffix = SUFFIXES[key as keyof typeof SUFFIXES];
		value = value.replaceAll(suffix, "{$rthSuffix}");
	}
	return value;
}

export function readMode(skipSetting = false): string {
	let type;
	if (signals.currentFile.value) {
		type = signals.currentFile.value.split(".").pop()?.toLowerCase();
		switch (type) {
			case "c":
			case "cpp":
			case "css":
			case "go":
			case "ini":
			case "java":
			case "json":
			case "less":
			case "lua":
			case "php":
			case "r":
			case "scss":
			case "sql":
			case "swift":
			case "vb":
			case "xml":
				break;
			case "coffee": {
				type = "coffeescript";
				break;
			}
			case "cs": {
				type = "csharp";
				break;
			}
			case "js":
			case "jsx": {
				type = "javascript";
				break;
			}
			case "m": {
				type = "objective-c";
				break;
			}
			case "md": {
				type = "markdown";
				break;
			}
			case "pas": {
				type = "pascal";
				break;
			}
			case "py": {
				type = "python";
				break;
			}
			case "rb": {
				type = "ruby";
				break;
			}
			case "rs": {
				type = "rust";
				break;
			}
			case "rth": {
				type = "json";
				break;
			}
			case "sh": {
				type = "shell";
				break;
			}
			case "svg": {
				type = "xml";
				break;
			}
			case "ts":
			case "tsx": {
				type = "typescript";
				break;
			}
			case "txt": {
				type = "plaintext";
				break;
			}
			default: {
				type = "html";
				break;
			}
		}
	} else {
		type = "markdown";
	}
	if (editorInstance && !skipSetting) {
		const model = editorInstance.getModel();
		if (model) {
			monaco.editor.setModelLanguage(model, type);
		}
	}
	return type;
}

export function redo(): void {
	if (editorInstance) {
		editorInstance.trigger("keyboard", "redo", null);
	} else {
		codeTextArea.focus();
		document.execCommand("redo");
	}
}

export function reloadMonacoEditor(): void {
	if (!editorInstance) {
		return;
	}
	clearModels(false);
	editorInstance.dispose();
	editorInstance = null;
	createMonacoEditor();
}

function setModel(filename: string): void {
	if (!editorInstance) {
		return;
	}
	const models = monaco.editor.getModels();
	let modelFound = false;
	for (const model of models) {
		if (model.uri.path === "/" + filename) {
			editorInstance.setModel(model);
			modelFound = true;
			break;
		}
	}
	if (!modelFound) {
		for (const model of models) {
			if (model.uri.path === "/") {
				editorInstance.setModel(model);
				break;
			}
		}
	}
}

export function setValue(
	value = "",
	options?: {
		filename?: string;
		willScrollToBottom?: boolean;
	},
): void {
	value = value.replaceAll("{$rthSuffix}", getSuffix());
	codeTextArea.value = value;
	if (editorInstance) {
		editorInstance.setScrollPosition({
			scrollTop: options?.willScrollToBottom
				? editorInstance.getScrollHeight()
				: 0,
		});
		setModel(options?.filename || "");
		editorInstance.setValue(value);
	} else {
		codeChanged();
	}
}

export function undo(): void {
	if (editorInstance) {
		editorInstance.trigger("keyboard", "undo", null);
	} else {
		codeTextArea.focus();
		document.execCommand("undo");
	}
}

export function wrap(): void {
	if (!codeTextArea.classList.contains("wrap")) {
		codeTextArea.classList.add("wrap");
		editorInstance?.updateOptions({
			wordWrap: "on",
		});
		void showToast(t("wordWrapOn"));
	} else {
		codeTextArea.classList.remove("wrap");
		editorInstance?.updateOptions({
			wordWrap: "off",
		});
		void showToast(t("wordWrapOff"));
	}
}
